import React, { useState, useEffect, useRef } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { ChevronDown, Building2, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CompanySwitcher() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await apiGet('/companies');
        setCompanies(list);
      } catch {
        /* silent */
      }
    })();
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  if (!companies || companies.length === 0) return null;

  const active = companies.find(c => c.is_active) || companies[0];

  const switchTo = async (cid) => {
    if (cid === active.id) { setOpen(false); return; }
    setSwitching(cid);
    try {
      const r = await apiPost(`/companies/switch/${cid}`, {});
      if (r.access_token) localStorage.setItem('access_token', r.access_token);
      localStorage.setItem('active_company_id', cid);
      localStorage.setItem('active_company_name', r.active_company?.name || '');
      toast.success(`Switched to ${r.active_company.name}`);
      // Reload to refresh all data scoped to new company
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Switch failed');
      setSwitching(null);
    }
  };

  // Hide entirely if user has only 1 company
  if (companies.length === 1) {
    return (
      <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-sm" data-testid="company-badge-static">
        <div
          className="w-5 h-5 rounded-sm flex items-center justify-center text-white text-[9px] font-bold"
          style={{ background: active?.primary_color || '#1F7A6E' }}
        >
          <Building2 size={10} />
        </div>
        <span className="text-xs font-display font-bold text-slate-700">{active?.name || '—'}</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="company-switcher">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 hover:border-[#1F7A6E] rounded-sm transition-colors"
        data-testid="company-switcher-btn"
      >
        <div
          className="w-5 h-5 rounded-sm flex items-center justify-center text-white"
          style={{ background: active?.primary_color || '#1F7A6E' }}
        >
          <Building2 size={10} />
        </div>
        <span className="text-xs font-display font-bold text-slate-800 max-w-[140px] truncate">{active?.name || 'Select Company'}</span>
        <ChevronDown size={12} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-sm shadow-xl z-50 overflow-hidden" data-testid="company-switcher-menu">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
            <div className="kpi-overline">Switch Company</div>
          </div>
          <div className="py-1">
            {companies.map(c => (
              <button
                key={c.id}
                onClick={() => switchTo(c.id)}
                disabled={switching === c.id}
                data-testid={`company-option-${c.short_key || c.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-teal-50 text-left transition-colors disabled:opacity-50"
              >
                <div
                  className="w-7 h-7 rounded-sm flex items-center justify-center text-white shrink-0"
                  style={{ background: c.primary_color || '#1F7A6E' }}
                >
                  <Building2 size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-display font-bold text-slate-800 truncate">{c.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{c.entity_type} {c.is_parent ? '· Parent' : ''}</div>
                </div>
                {switching === c.id ? (
                  <Loader2 size={14} className="animate-spin text-[#1F7A6E] shrink-0" />
                ) : c.is_active ? (
                  <Check size={14} className="text-emerald-600 shrink-0" />
                ) : null}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
